import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';

const NAV_EXPANDED_KEY = 'harry.navExpanded';

interface ShellContextValue {
  navExpanded: boolean;
  navOpen: boolean;
  coachOpen: boolean;
  /** In-context Help sheet (header Help button). */
  helpOpen: boolean;
  isMobile: boolean;
  toggleNav: () => void;
  closeNav: () => void;
  openCoach: () => void;
  closeCoach: () => void;
  toggleCoach: () => void;
  openHelp: () => void;
  closeHelp: () => void;
}

const ShellContext = createContext<ShellContextValue | null>(null);

export function ShellProvider({ children }: { children: React.ReactNode }) {
  const isMobile = useIsMobile();
  const [navExpanded, setNavExpanded] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(NAV_EXPANDED_KEY) === '1';
  });
  const [navOpen, setNavOpen] = useState(false);
  const [coachOpen, setCoachOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    if (!isMobile) {
      localStorage.setItem(NAV_EXPANDED_KEY, navExpanded ? '1' : '0');
    }
  }, [navExpanded, isMobile]);

  useEffect(() => {
    if (!isMobile) {
      setNavOpen(false);
    } else {
      setNavExpanded(false);
    }
  }, [isMobile]);

  const closeNav = useCallback(() => {
    if (isMobile) {
      setNavOpen(false);
    } else {
      setNavExpanded(false);
    }
  }, [isMobile]);

  const toggleNav = useCallback(() => {
    if (isMobile) {
      setNavOpen((open) => !open);
    } else {
      setNavExpanded((expanded) => !expanded);
    }
  }, [isMobile]);

  const openCoach = useCallback(() => {
    setCoachOpen(true);
    setNavOpen(false);
  }, []);

  const closeCoach = useCallback(() => setCoachOpen(false), []);

  const toggleCoach = useCallback(() => {
    setCoachOpen((open) => !open);
  }, []);

  const openHelp = useCallback(() => {
    setHelpOpen(true);
    setCoachOpen(false);
    setNavOpen(false);
  }, []);

  const closeHelp = useCallback(() => setHelpOpen(false), []);

  const value = useMemo(
    () => ({
      navExpanded,
      navOpen,
      coachOpen,
      helpOpen,
      isMobile,
      toggleNav,
      closeNav,
      openCoach,
      closeCoach,
      toggleCoach,
      openHelp,
      closeHelp,
    }),
    [navExpanded, navOpen, coachOpen, helpOpen, isMobile, toggleNav, closeNav, openCoach, closeCoach, toggleCoach, openHelp, closeHelp]
  );

  return <ShellContext.Provider value={value}>{children}</ShellContext.Provider>;
}

export function useShell() {
  const ctx = useContext(ShellContext);
  if (!ctx) {
    throw new Error('useShell must be used within ShellProvider');
  }
  return ctx;
}

/** Mobile drawer backdrop — rendered inside ShellProvider. */
export function NavBackdrop() {
  const { navOpen, isMobile, closeNav } = useShell();
  if (!isMobile || !navOpen) return null;
  return (
    <button
      type="button"
      className="fixed inset-0 z-40 bg-black/40 md:hidden"
      aria-label="Close navigation"
      onClick={closeNav}
    />
  );
}
